// Auto-identify the logged-in user in InsightFlare.
//
// Strategy (covers all entry paths):
//   - On mount / when InsightFlare becomes ready: read localStorage and identify.
//   - On `storage` events: catches logins/logouts from OTHER tabs.
//   - On a light interval: catches same-tab logins (the `storage` event does NOT
//     fire in the tab that wrote the value), and logout in the same tab.
//
// When the auth token disappears we call reset() so the analytics session is
// detached from the previous user.
import { useEffect, useRef } from 'react';
import { useInsightFlare } from '@/contexts/InsightFlareContext';

function readUser(): { userId: string; userName: string } | null {
  if (typeof window === 'undefined') return null;
  const token = localStorage.getItem('genofx:token');
  if (!token) return null; // not logged in

  const username = localStorage.getItem('genofx:username');
  const email = localStorage.getItem('genofx:email');
  const userJson = localStorage.getItem('genofx:user');

  let userData: any = null;
  if (userJson) {
    try {
      userData = JSON.parse(userJson);
    } catch {
      /* ignore malformed */
    }
  }

  const userId = username || email || userData?.id;
  if (!userId) return null;

  const userName = userData?.name || username || email || 'User';
  return { userId: String(userId), userName: String(userName) };
}

export const useAutoIdentifyUser = () => {
  const { identify, reset, isReady } = useInsightFlare();
  // Track the last identity we pushed so we don't spam identify/reset.
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady) return;

    const sync = () => {
      const current = readUser();

      if (current) {
        if (current.userId !== lastUserId.current) {
          lastUserId.current = current.userId;
          identify(current.userId, current.userName);
        }
      } else if (lastUserId.current !== null) {
        // Was identified, now logged out.
        lastUserId.current = null;
        reset();
      }
    };

    // Initial sync + listeners.
    sync();
    window.addEventListener('storage', sync);
    const interval = window.setInterval(sync, 2000);

    return () => {
      window.removeEventListener('storage', sync);
      window.clearInterval(interval);
    };
  }, [isReady, identify, reset]);
};
